import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Footer from '../../components/ui/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabase';

// Import existing components
import SavedVendors from './components/SavedVendors';
import WeddingCountdown from './components/WeddingCountdown';
import WeddingChecklist from './components/WeddingChecklist';
import ProfileCompletionProgress from './components/ProfileCompletionProgress';
import VendorSuggestions from './components/VendorSuggestions';
import RecentlyViewedVendors from './components/RecentlyViewedVendors';
import UpcomingEvents from './components/UpcomingEvents';
import QuickContactSummary from './components/QuickContactSummary';
import MessageCenter from './components/MessageCenter';
import ReferralBadge from './components/ReferralBadge';
import referralService from '../../services/referralService';

// Import planning tools components
import WeddingChecklistSection from '../user-dashboard-wedding-planning-tools/components/WeddingChecklistSection';
import BudgetCalculator from '../user-dashboard-wedding-planning-tools/components/BudgetCalculator';
import GuestListManager from '../user-dashboard-wedding-planning-tools/components/GuestListManager';
import RSVPManager from '../user-dashboard-wedding-planning-tools/components/RSVPManager';

// Import profile components
import ProfileSection from '../user-dashboard-clean-mobile-layout/components/ProfileSection';
import PreferencesSection from '../user-dashboard-clean-mobile-layout/components/PreferencesSection';

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user: authUser, userProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [referralHistory, setReferralHistory] = useState([]);
  const [referralStats, setReferralStats] = useState(null);

  // Load user profile data from Supabase
  useEffect(() => {
    const loadUserData = async () => {
      if (!authUser?.id) {
        navigate('/sign-in-page');
        return;
      }

      setIsLoading(true);

      try {
        // Get user profile with wedding details
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', authUser.id)
          .single();

        if (error) throw error;

        setUserDetails({
          name: profile.full_name || authUser.email?.split('@')[0],
          email: authUser.email,
          phone: profile.phone || '',
          profileImage: profile.profile_image || authUser.user_metadata?.avatar_url || '',
          weddingDate: profile.wedding_date || null,
          partner: profile.partner_name || '',
          location: profile.location || profile.city ? `${profile.city}, ${profile.state || ''}` : '',
          culture: profile.culture || '',
          budget: profile.wedding_budget || 0,
          guestCount: profile.guest_count || 0,
          planningStartDate: profile.created_at?.split('T')[0] || new Date().toISOString().split('T')[0]
        });

        // Load referral data
        const [historyResult, statsResult] = await Promise.all([
          referralService.getReferralHistory(authUser.id, { limit: 5 }),
          referralService.getUserReferralStats(authUser.id)
        ]);

        if (historyResult.success) {
          setReferralHistory(historyResult.data || []);
        }

        if (statsResult.success) {
          setReferralStats(statsResult.data);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, [authUser, navigate]);

  // Calculate days until wedding
  const getDaysUntilWedding = () => {
    if (!userDetails?.weddingDate) return null;
    const today = new Date();
    const weddingDate = new Date(userDetails.weddingDate);
    const timeDiff = weddingDate.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysDiff > 0 ? daysDiff : 0;
  };

  // Comprehensive navigation items
  const navigationItems = [
    {
      id: 'overview',
      label: 'Overview',
      icon: 'Home',
      description: 'Dashboard overview'
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: 'User',
      description: 'Manage your profile'
    },
    {
      id: 'checklist',
      label: 'Checklist',
      icon: 'CheckSquare',
      description: 'Wedding planning tasks'
    },
    {
      id: 'vendors',
      label: 'Vendors',
      icon: 'Building',
      description: 'Saved & suggested vendors'
    },
    {
      id: 'planning-tools',
      label: 'Planning Tools',
      icon: 'Calculator',
      description: 'Budget, guests, RSVP'
    },
    {
      id: 'messages',
      label: 'Messages',
      icon: 'MessageSquare',
      description: 'Communications'
    },
    {
      id: 'events',
      label: 'Events',
      icon: 'Calendar',
      description: 'Upcoming events'
    },
    {
      id: 'referrals',
      label: 'Referrals',
      icon: 'Gift',
      description: 'Referral rewards'
    },
    {
      id: 'preferences',
      label: 'Preferences',
      icon: 'Settings',
      description: 'Account settings'
    }
  ];



  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    setSidebarOpen(false);
  };

  const renderMainContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-4 sm:space-y-6 w-full max-w-full overflow-x-hidden">
            {/* Welcome Header */}
            <div className="text-center px-2">
              <h1 className="font-heading font-bold text-xl sm:text-2xl lg:text-3xl text-text-primary mb-2">
                Welcome back, {userDetails?.name?.split(' ')[0] || 'there'}!
              </h1>
              <p className="text-text-secondary text-sm sm:text-base lg:text-lg">
                Let's make your wedding planning journey magical
              </p>
            </div>

            {/* Wedding Countdown - Full width, mobile optimized */}
            <div className="w-full">
              <WeddingCountdown weddingDate={userDetails?.weddingDate || userProfile?.wedding_date} />
            </div>

            {/* Main Dashboard Grid - Mobile-first responsive layout */}
            <div className="space-y-4 sm:space-y-6">
              {/* Profile Progress - Always first on mobile */}
              <div className="w-full">
                <ProfileCompletionProgress onSectionChange={handleSectionChange} />
              </div>

              {/* Two-column layout for larger screens, stacked for mobile */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Left Column Content */}
                <div className="space-y-4 sm:space-y-6">
                  <WeddingChecklist />
                  {/* Show QuickContactSummary on mobile here */}
                  <div className="lg:hidden">
                    <QuickContactSummary onSectionChange={handleSectionChange} />
                  </div>
                </div>

                {/* Right Column Content */}
                <div className="space-y-4 sm:space-y-6">
                  <SavedVendors />
                  <MessageCenter />
                  {/* Hide QuickContactSummary on mobile, show on desktop */}
                  <div className="hidden lg:block">
                    <QuickContactSummary onSectionChange={handleSectionChange} />
                  </div>
                </div>
              </div>

              {/* Full Width Sections - Stack vertically with proper spacing */}
              <div className="space-y-4 sm:space-y-6">
                <ReferralBadge />
                <VendorSuggestions />
                <RecentlyViewedVendors />
                <UpcomingEvents />
              </div>
            </div>
          </div>
        );

      case 'profile':
        return <ProfileSection />;

      case 'checklist':
        return (
          <div className="space-y-4 sm:space-y-6 w-full max-w-full overflow-x-hidden">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <h2 className="font-heading font-bold text-lg sm:text-xl lg:text-2xl text-text-primary">
                Wedding Checklist
              </h2>
            </div>
            <WeddingChecklistSection />
          </div>
        );

      case 'vendors':
        return (
          <div className="space-y-4 sm:space-y-6 w-full max-w-full overflow-x-hidden">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <h2 className="font-heading font-bold text-lg sm:text-xl lg:text-2xl text-text-primary">
                My Vendors
              </h2>
              <Button
                variant="primary"
                iconName="Plus"
                onClick={() => navigate('/category-browse')}
                className="w-full sm:w-auto text-sm sm:text-base"
              >
                Find Vendors
              </Button>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <SavedVendors />
              <RecentlyViewedVendors />
              <VendorSuggestions />
            </div>
          </div>
        );

      case 'planning-tools':
        return (
          <div className="space-y-4 sm:space-y-6 w-full max-w-full overflow-x-hidden">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <h2 className="font-heading font-bold text-lg sm:text-xl lg:text-2xl text-text-primary">
                Wedding Planning Tools
              </h2>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <BudgetCalculator totalBudget={userDetails?.budget} />
                <GuestListManager totalGuests={userDetails?.guestCount} />
              </div>

              <RSVPManager />
            </div>
          </div>
        );

      case 'messages':
        return (
          <div className="space-y-4 sm:space-y-6 w-full max-w-full overflow-x-hidden">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <h2 className="font-heading font-bold text-lg sm:text-xl lg:text-2xl text-text-primary">
                Messages
              </h2>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <MessageCenter />
              <div className="lg:hidden">
                <QuickContactSummary />
              </div>
            </div>
          </div>
        );

      case 'events':
        return (
          <div className="space-y-4 sm:space-y-6 w-full max-w-full overflow-x-hidden">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <h2 className="font-heading font-bold text-lg sm:text-xl lg:text-2xl text-text-primary">
                Events & Promotions
              </h2>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <UpcomingEvents />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <WeddingCountdown weddingDate={userDetails?.weddingDate || userProfile?.wedding_date} />
                <div className="bg-white rounded-xl shadow-soft p-4 sm:p-6">
                  <h3 className="font-heading font-semibold text-base sm:text-lg lg:text-xl text-text-primary mb-4">
                    Event Calendar
                  </h3>
                  <div className="text-center py-6 sm:py-8">
                    <Icon name="Calendar" size={48} className="text-text-muted mx-auto mb-4" />
                    <p className="text-text-secondary text-sm sm:text-base">
                      Your personalized event calendar with important wedding milestones and vendor appointments
                    </p>
                    <Button
                      variant="primary"
                      size="sm"
                      iconName="Plus"
                      className="mt-4"
                      onClick={() => onSectionChange('budget')}
                    >
                      Add Event
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'referrals':
        return (
          <div className="space-y-4 sm:space-y-6 w-full max-w-full overflow-x-hidden">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <h2 className="font-heading font-bold text-lg sm:text-xl lg:text-2xl text-text-primary">
                Referral Program
              </h2>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <ReferralBadge />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div className="bg-white rounded-xl shadow-soft p-4 sm:p-6">
                  <h3 className="font-heading font-semibold text-base sm:text-lg lg:text-xl text-text-primary mb-4">
                    Referral History
                  </h3>
                  <div className="space-y-3">
                    {referralHistory.length === 0 ? (
                      <div className="text-center py-6">
                        <Icon name="Users" size={32} className="text-text-muted mx-auto mb-2" />
                        <p className="text-sm text-text-secondary">No referrals yet</p>
                        <p className="text-xs text-text-muted mt-1">Start inviting friends!</p>
                      </div>
                    ) : (
                      referralHistory.slice(0, 2).map((referral) => {
                        const referredName = referral.referred_user?.full_name || referral.referred_email?.split('@')[0] || 'Friend';
                        const joinedDate = new Date(referral.created_at);
                        const daysAgo = Math.floor((Date.now() - joinedDate.getTime()) / (1000 * 60 * 60 * 24));
                        const timeText = daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : daysAgo < 7 ? `${daysAgo} days ago` : daysAgo < 30 ? `${Math.floor(daysAgo / 7)} week${Math.floor(daysAgo / 7) > 1 ? 's' : ''} ago` : `${Math.floor(daysAgo / 30)} month${Math.floor(daysAgo / 30) > 1 ? 's' : ''} ago`;
                        
                        return (
                          <div key={referral.id} className="flex items-center justify-between p-3 bg-surface-50 rounded-lg">
                            <div>
                              <p className="font-medium text-text-primary text-sm">{referredName}</p>
                              <p className="text-xs text-text-secondary">{timeText}</p>
                            </div>
                            {referral.status === 'completed' && (
                              <span className="text-green-600 text-sm font-medium">+${referral.reward_amount}</span>
                            )}
                            {referral.status === 'pending' && (
                              <span className="text-yellow-600 text-xs font-medium">Pending</span>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Eye"
                    className="w-full mt-4"
                    onClick={() => navigate('/referral-history')}
                  >
                    View All Referrals
                  </Button>
                </div>
                <div className="bg-white rounded-xl shadow-soft p-4 sm:p-6">
                  <h3 className="font-heading font-semibold text-base sm:text-lg lg:text-xl text-text-primary mb-4">
                    Rewards Earned
                  </h3>
                  <div className="space-y-3">
                    <div className="text-center p-4 bg-primary-50 rounded-lg">
                      <div className="text-2xl font-bold text-primary">${referralStats?.totalRewards || 0}</div>
                      <div className="text-sm text-text-secondary">Total Earned</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-center">
                      <div className="p-3 bg-green-50 rounded-lg">
                        <div className="text-lg font-bold text-green-600">
                          ${referralStats?.referrals?.filter(r => r.status === 'completed' && !r.is_redeemed).reduce((sum, r) => sum + r.reward_amount, 0) || 0}
                        </div>
                        <div className="text-xs text-text-secondary">Available</div>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <div className="text-lg font-bold text-blue-600">
                          ${referralStats?.referrals?.filter(r => r.is_redeemed).reduce((sum, r) => sum + r.reward_amount, 0) || 0}
                        </div>
                        <div className="text-xs text-text-secondary">Used</div>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    iconName="Gift"
                    className="w-full mt-4"
                    onClick={() => navigate('/referral-history')}
                    disabled={!referralStats?.referrals?.some(r => r.status === 'completed' && !r.is_redeemed)}
                  >
                    Redeem Rewards
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'preferences':
        return <PreferencesSection />;

      default:
        return <ProfileSection />;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-text-secondary">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Helmet>
        <title>Wedding Dashboard - Plan Your Dream Wedding | ShaadiNest</title>
        <meta name="description" content="Manage your wedding planning journey with personalized vendor matches, saved favorites, and comprehensive planning tools." />
      </Helmet>

      <Header />

      <div className="pt-16">
        <div className="flex max-w-full overflow-x-hidden">
          {/* Desktop Sidebar - Hidden on mobile and tablet */}
          <div className="hidden xl:block w-72 2xl:w-80 flex-shrink-0 bg-white shadow-soft border-r border-border min-h-screen sticky top-16">
            <div className="p-4 2xl:p-6 overflow-y-auto max-h-screen">
              {/* User Profile Summary */}
              <div className="text-center mb-4 2xl:mb-6">
                <div className="relative mx-auto mb-3 2xl:mb-4">
                  <img
                    src={userDetails?.profileImage}
                    alt={userDetails?.name}
                    className="w-16 h-16 2xl:w-20 2xl:h-20 rounded-full object-cover mx-auto border-4 border-primary-100"
                  />
                  <div className="absolute bottom-0 right-1/2 transform translate-x-1/2 translate-y-1/2 w-5 h-5 2xl:w-6 2xl:h-6 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
                <h2 className="font-heading font-semibold text-lg 2xl:text-xl text-text-primary">
                  {userDetails?.name}
                </h2>
                <p className="text-text-secondary text-xs 2xl:text-sm">{userDetails?.email}</p>
                <div className="mt-2 px-2 2xl:px-3 py-1 bg-primary-50 text-primary text-xs rounded-full inline-block">
                  {getDaysUntilWedding()} days to go!
                </div>
              </div>

              {/* Navigation */}
              <nav className="space-y-1 2xl:space-y-2">
                {navigationItems?.map((item) => (
                  <button
                    key={item?.id}
                    onClick={() => handleSectionChange(item?.id)}
                    className={`w-full flex items-center space-x-2 2xl:space-x-3 px-3 2xl:px-4 py-2 2xl:py-3 rounded-lg transition-colors text-left ${activeSection === item?.id
                      ? 'bg-primary text-white shadow-soft'
                      : 'text-text-secondary hover:bg-surface-100'
                      }`}
                  >
                    <Icon name={item?.icon} size={18} className="2xl:w-5 2xl:h-5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm 2xl:text-base">{item?.label}</div>
                      <div className="text-xs opacity-80 truncate">{item?.description}</div>
                    </div>
                  </button>
                ))}
              </nav>

              {/* Quick Actions */}
              <div className="mt-4 2xl:mt-6 pt-4 2xl:pt-6 border-t border-border-light">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Search"
                  fullWidth
                  onClick={() => navigate('/category-browse')}
                  className="text-sm"
                >
                  Find Vendors
                </Button>
              </div>
            </div>
          </div>

          {/* Mobile Sidebar */}
          <motion.div
            initial={false}
            animate={{
              x: sidebarOpen ? 0 : -320
            }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed left-0 top-16 bottom-0 w-80 bg-white shadow-soft border-r border-border overflow-y-auto z-50 xl:hidden"
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <img
                    src={userDetails?.profileImage}
                    alt={userDetails?.name}
                    className="w-10 h-10 rounded-full object-cover border-2 border-primary-100"
                  />
                  <div>
                    <h2 className="font-semibold text-text-primary text-sm">{userDetails?.name?.split(' ')[0]}</h2>
                    <p className="text-xs text-text-muted">Wedding Dashboard</p>
                  </div>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 rounded-lg hover:bg-surface-100 transition-colors"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>

              <nav className="space-y-1">
                {navigationItems?.map((item) => (
                  <button
                    key={item?.id}
                    onClick={() => handleSectionChange(item?.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg transition-colors text-left ${activeSection === item?.id
                      ? 'bg-primary text-white shadow-soft'
                      : 'text-text-secondary hover:bg-surface-100'
                      }`}
                  >
                    <Icon name={item?.icon} size={20} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{item?.label}</div>
                      <div className="text-xs opacity-80 truncate">{item?.description}</div>
                    </div>
                  </button>
                ))}
              </nav>

              <div className="mt-4 pt-4 border-t border-border-light">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Search"
                  fullWidth
                  onClick={() => navigate('/category-browse')}
                  className="text-sm"
                >
                  Find Vendors
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Mobile overlay */}
          {sidebarOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40 xl:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Main Content */}
          <main className="flex-1 xl:ml-0 min-h-screen w-full overflow-x-hidden">
            {/* Mobile header - Optimized for touch */}
            <div className="xl:hidden flex items-center justify-between p-3 sm:p-4 bg-white border-b border-border sticky top-16 z-30">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-lg bg-surface-100 hover:bg-surface-200 transition-colors touch-manipulation"
              >
                <Icon name="Menu" size={20} />
              </button>
              <h1 className="text-sm sm:text-base lg:text-lg font-semibold text-text-primary truncate mx-4">
                {navigationItems?.find(item => item?.id === activeSection)?.label || 'Dashboard'}
              </h1>
              <div className="w-10" />
            </div>

            {/* Content with mobile-optimized padding */}
            <div className="p-3 sm:p-4 lg:p-6 xl:p-8 w-full max-w-7xl mx-auto">
              <motion.div
                key={activeSection}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {renderMainContent()}
              </motion.div>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default UserDashboard;